import { isAudioDiscovery } from '#~/device/discovery/index.js';
import { isAudioDevice } from '#~/device/index.js';
import { isTransient } from '@studio/models/device';
import { useLazyRef, χref } from '@studio/react-utils/hooks';
import { freeze } from 'immer';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import { Context } from './context.js';
import type { Draft, Props, State, Store } from './types.js';
import { Panel } from './types.js';
import { isDiscoveryInUse } from './utils.js';
import { generateFlowId } from '#~/analytics/pageview/index.js';

/** Add audio source workflow `<Provider>`. */
export const Provider = ({
  agentIds,
  appId,
  children,
  devices,
  discoveries,
  roomId
}: Props): ReactElement => {
  const store = useLazyRef<Store>(() =>
    create<State>()(
      immer((set) =>
        freeze({
          agentId: agentIds[0]!,
          appId,
          candidate: {},
          flowId: generateFlowId(),
          panel: Panel.Source,
          previews: {},
          produce: (fn: (draft: Draft) => void): void => {
            set(fn);
          },
          roomId,
          settings: {
            isReady: false,
            isSaving: false
          },
          source: {
            discoveries: [],
            existing: [],
            isReady: false,
            selection: undefined
          }
        })
      )
    )
  );

  // Inject external state.
  useEffect(() => {
    const { produce } = χref(store).getState();

    produce(({ source }) => {
      // Keep track of existing audio devices.
      source.existing = freeze(
        Object.values(devices)
          .filter((µ) => !isTransient(µ.id))
          .filter(isAudioDevice)
      );

      // Keep track of discovered audio devices.
      source.discoveries = freeze(
        Object.values(discoveries)
          .filter(isAudioDiscovery)
          .filter(
            (discovery) =>
              // Filter out discoveries that are already in use.
              !source.existing.some((device) => isDiscoveryInUse(device, discovery))
          )
      );
    });
  }, [devices, discoveries]);

  return <Context.Provider value={χref(store)}>{children}</Context.Provider>;
};
